import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { ViewpointRow, ViewpointFilters } from '@/types';

// Convert yyyymmdd to yyyy-mm-dd for Supabase
function formatDateForDb(ymd: string): string {
  return `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}`;
}

export function useViewpoints(filters: ViewpointFilters) {
  return useQuery({
    queryKey: ['viewpoints', filters],
    queryFn: async (): Promise<ViewpointRow[]> => {
      let query = supabase
        .from('ticker_viewpoints')
        .select('*')
        .order('date', { ascending: false })
        .order('channel_name', { ascending: true })
        .limit(500);

      // Apply date filter (single date for daily view)
      if (filters.date) {
        query = query.eq('date', formatDateForDb(filters.date));
      }

      // Apply date range filter (for ticker page)
      if (filters.dateRange?.startDate && filters.dateRange?.endDate) {
        query = query
          .gte('date', formatDateForDb(filters.dateRange.startDate))
          .lte('date', formatDateForDb(filters.dateRange.endDate));
      }

      // Apply single ticker filter (for ticker page)
      if (filters.ticker) {
        query = query.eq('ticker', filters.ticker.toUpperCase());
      }

      // Apply multi-ticker filter (for date view)
      if (filters.tickers.length > 0) {
        query = query.in('ticker', filters.tickers.map(t => t.toUpperCase()));
      }

      // Apply single channel filter (for blogger page)
      if (filters.channel) {
        query = query.eq('channel_name', filters.channel);
      }

      // Apply multi-channel filter (for date view)
      if (filters.channels.length > 0) {
        query = query.in('channel_name', filters.channels);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Supabase error:', error);
        throw new Error('数据加载失败');
      }

      let results = (data || []) as ViewpointRow[];

      // Post-filter for onlyExplicit if needed
      if (filters.onlyExplicit) {
        results = results.filter(row => {
          const refs = row.reference_data || [];
          return refs.some(ref =>
            (ref.ticker_source || '').toLowerCase() !== 'inferred_other'
          );
        });
      }

      return results;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    enabled: !!(filters.date || filters.ticker || filters.channel), // Fetch when date, ticker, or channel is selected
  });
}
